/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleCalendar from "./GoogleCalendar";
import CalendarContext from "components/Calendar/CalendarContext";
import AppContext from "AppContext";
import { Switch, Route } from "react-router-dom";
import GoogleCalendarLogin from "./GoogleCalendarLogin";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifyInfo, notifySuccess, notifyWarning } from "redux/actions/Notifications";
import { useHistory } from "react-router-dom";
import Page from "components/common_components/Page";
import { buildSetGoingSuccessMessage, calculateRange } from "components/Calendar/utils";
import { hasElements } from "utils/arrays";
import { useEventUtils } from "./utils";
import { now } from "utils/dateUtils";
import Filters from "./Filters";

const DEFAULT_RESOURCES = { users: [], calendars: [] };
const DEFAULT_VIEW = "week";
const DEFAULT_DATE = now();

export default function GoogleCalendarIndex() {
    const dispatch = useDispatch();
    const history = useHistory();
    const { user } = useSelector((state) => state.Login);
    const [resources, setResources] = useState(DEFAULT_RESOURCES);
    const [filters, setFilters] = useState({ moduleCode: "all", fileNo: "", clientName: "" });
    const [events, setEvents] = useState([]);
    const GoogleCalendarAPIRef = useRef({});
    const GoogleCalendarAPI = () => GoogleCalendarAPIRef.current;
    const [selectedCalendars, setSelectedCalendars] = useState([]);
    const [view, setView] = useState(DEFAULT_VIEW);
    const [date, setDate] = useState(DEFAULT_DATE);
    const EventUtils = useEventUtils({ ...resources, user, selectedCalendars });
    // E.g = [ { email: "xx@dd.cm" } ]
    const [guests, setGuests] = useState([]);
    // E.g = [ { email: "xx@dd.cm", events: [], color: "#a01e1e" } ]
    const [guestCalendars, setGuestCalendars] = useState([]);

    const {
        api: {
            Google,
            Settings: { Users },
        },
    } = useContext(AppContext);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
            },
        });
    }

    const loadResources = () => {
        if (hasSessionData()) {
            onSilentAuthSuccess();
        } else {
            history.push("/master-calendar/login");
        }
        getUsers();
        getCalendars();
    };

    const clearResources = () => {
        setResources(DEFAULT_RESOURCES);
    };

    useEffect(() => {
        if (hasElements(resources.calendars)) {
            const preSelectedCalendars = resources.calendars.filter((x) => x.selected);
            if (hasElements(preSelectedCalendars)) {
                setSelectedCalendars(preSelectedCalendars);
            } else {
                setSelectedCalendars([]);
                dispatch(notifyWarning("Please select at least one calendar in Google Calendar."));
            }
        } else {
            setSelectedCalendars([]);
        }
    }, [resources.calendars]);

    const toggleCalendarAsSelected = (calendarId) => {
        setResources((prev) => ({
            ...prev,
            calendars: prev.calendars.map((calendar) => {
                if (calendar.id === calendarId) {
                    return {
                        ...calendar,
                        selected: !calendar.selected,
                    };
                }
                return calendar;
            }),
        }));
    };

    useEffect(() => {
        const { start, end } = calculateRange(view, date);
        getEvents(start, end);
    }, [selectedCalendars, filters, view, date]);

    const hasSessionData = () => {
        return Boolean(localStorage.getItem("_goo_t")) && Boolean(localStorage.getItem("_goo_rt"));
    };

    const storeSessionData = async (token, refreshToken, expiresInSeconds) => {
        if (token) localStorage.setItem("_goo_t", token);
        if (refreshToken) localStorage.setItem("_goo_rt", refreshToken);
        if (expiresInSeconds) localStorage.setItem("_goo_exps", expiresInSeconds);
        startTokenRefreshJob();
    };

    const startTokenRefreshJob = () => {
        const expiresInSeconds = localStorage.getItem("_goo_exps");
        const existingInterval = localStorage.getItem("_goo_interval_id");
        if (existingInterval) {
            window.clearInterval(existingInterval);
        }
        const intervalId = window.setInterval(async () => {
            try {
                const refreshToken = localStorage.getItem("_goo_rt");
                console.log("Refreshing Google token...");
                const googleAuthResponse = await Google.Auth.refreshToken(refreshToken);
                const { access_token, expires_in } = googleAuthResponse.data;
                storeSessionData(access_token, undefined, expires_in);
                onAuthSuccess();
            } catch (error) {
                console.error(error);
            }
        }, (expiresInSeconds - 300) * 1000);
        localStorage.setItem("_goo_interval_id", intervalId);
    };

    const onSilentAuthSuccess = () => {
        startTokenRefreshJob();
        initAPI();
    };

    const onAuthSuccess = (data) => {
        const { access_token, refresh_token, expires_in } = data;
        storeSessionData(access_token, refresh_token, expires_in);
        initAPI();
    };

    const initAPI = () => {
        GoogleCalendarAPIRef.current = Google.CalendarAPI(`Bearer ${localStorage.getItem("_goo_t")}`);
        history.push("/master-calendar");
    };

    const processError = (error, failOnUnauthorized = true) => {
        console.error(error?.response);
        const apiResponseError = error?.response?.data?.error?.message;
        const internalError = error?.message;
        const errMsg = apiResponseError || internalError || "Something went wrong.";
        dispatch(notifyError(errMsg));
        if ([401, 403].includes(error?.response?.status) && failOnUnauthorized) {
            GoogleCalendarAPIRef.current = {};
            history.push("/master-calendar/login");
        }
    };

    const executeRequest = async (request, ...params) => {
        if (!request) {
            console.error("Google Calendar API not initialized.");
            history.push("/master-calendar/login");
            return;
        }
        try {
            const response = await request(...params);
            return response;
        } catch (error) {
            throw error;
        }
    };

    const getCalendars = async () => {
        try {
            const calendars = await executeRequest(GoogleCalendarAPI().getCalendarsAsync);
            setResources((prev) => ({
                ...prev,
                calendars,
            }));
        } catch (error) {
            processError(error);
        }
    };

    const getEvents = async (timeMin, timeMax) => {
        try {
            if (hasElements(selectedCalendars)) {
                let newFilters = Object.keys(filters)
                    .filter((key) => Boolean(filters[key]))
                    .reduce((obj, key) => ({ ...obj, [key]: filters[key] }), {});
                if (newFilters.moduleCode === "all") {
                    delete newFilters.moduleCode;
                }
                const promises = selectedCalendars.map((calendar) =>
                    executeRequest(
                        GoogleCalendarAPI().getEventsAsync,
                        calendar.id,
                        newFilters,
                        timeMin.toISOString(),
                        timeMax.toISOString()
                    )
                );
                Promise.all(promises)
                    .then((responses) => {
                        setEvents(responses.flat());
                    })
                    .catch((error) => {
                        processError(error);
                    });
            } else {
                setEvents([]);
            }
        } catch (error) {
            processError(error);
        }
    };

    const getEvent = async (calendarId, eventId) => {
        const evt = await executeRequest(GoogleCalendarAPI().getEventAsync, calendarId, eventId);
        return evt;
    };

    const addEvent = async (calendarId, event) => {
        const response = await executeRequest(GoogleCalendarAPI().addEventAsync, calendarId, event);
        return response;
    };

    const updateEvent = async (calendarId, event) => {
        const response = await executeRequest(GoogleCalendarAPI().updateEventAsync, calendarId, event.id, event);
        return response;
    };

    const deleteEvent = async (event) => {
        try {
            await executeRequest(GoogleCalendarAPI().deleteEventAsync, event.calendar.id, event.id);
            dispatch(notifyInfo(`The event "${event.title}" has been deleted.`));
        } catch (error) {
            processError(error);
        }
    };

    const onGoingHandler = async (attendeeEmail, answer, comment, event) => {
        try {
            const existingEvent = await getEvent(event.calendar.id, event.id);
            const responseStatus = answer === null ? "needsAction" : answer ? "accepted" : "declined";
            if (hasElements(existingEvent.attendees)) {
                existingEvent.attendees = existingEvent.attendees.map((attendee) => {
                    if (attendee.email === attendeeEmail) {
                        return {
                            ...attendee,
                            responseStatus,
                            comment,
                        };
                    }
                    return attendee;
                });
            }
            const updatedEvent = await updateEvent(event.calendar.id, existingEvent);
            const notifier = answer ? notifySuccess : notifyInfo;
            dispatch(notifier(buildSetGoingSuccessMessage(answer, comment)));
            return updatedEvent;
        } catch (error) {
            processError(error);
        }
    };

    const saveEvent = async ({ mode, event, onSuccess, onError, setSubmitting }) => {
        try {
            if (mode === "add") {
                const googleEvent = EventUtils.toGoogleEvent(event).forCreate();
                const response = await addEvent(event.calendar.id, googleEvent);
                onSuccess?.(response);
            } else {
                const existingEvent = await getEvent(event.calendar.id, event.id);
                const googleEvent = EventUtils.toGoogleEvent(event).forUpdate(existingEvent);
                const response = await updateEvent(event.calendar.id, googleEvent);
                onSuccess?.(response);
            }
        } catch (error) {
            processError(error);
            onError?.(error);
        } finally {
            setSubmitting?.(false);
        }
    };

    const onGuestsChange = (newGuests) => {
        if (!hasElements(newGuests)) {
            setGuests([]);
            setGuestCalendars([]);
            return;
        }

        const guestsToAdd = newGuests.filter((g) => !guests.find((g2) => g2.email === g.email));
        const guestsToRemove = guests.filter((g) => !newGuests.find((g2) => g2.email === g.email));
        setGuests(newGuests);

        // Remove Guest Calendars
        const filteredCalendars = guestCalendars.filter(
            (guestCalendar) => !guestsToRemove.find((guest) => guest.email === guestCalendar.email)
        );
        setGuestCalendars(filteredCalendars);

        // Add Guest Calendars
        if (hasElements(guestsToAdd)) {
            const { start, end } = calculateRange(view, date);
            const promises = guestsToAdd.map((guest) =>
                executeRequest(GoogleCalendarAPI().getGuestEventsAsync, guest.email, start.toISOString(), end.toISOString())
            );
            Promise.all(promises)
                .then((responses) => {
                    // E.g responses = [ { email, events } ]
                    if (hasElements(responses)) {
                        const guestCalendars = responses.map((response) => ({
                            ...response,
                            color: EventUtils.getColorForGuestCalendar(),
                        }));
                        setGuestCalendars((prev) => [...prev, ...guestCalendars]);
                    }
                })
                .catch((error) => {
                    console.warn("Couldn't get guest events", error);
                });
        }
    };

    return (
        <Page className="master-calendar-page">
            <CalendarContext.Provider
                value={{
                    user,
                    date,
                    setDate,
                    view,
                    setView,
                    filters,
                    setFilters,
                    events,
                    getEvents,
                    addEvent,
                    updateEvent,
                    onAuthSuccess,
                    resources,
                    onGoingHandler,
                    deleteEvent,
                    saveEvent,
                    styleGetter: EventUtils.styleGetter,
                    eventMappers: {
                        from: EventUtils.fromGoogleEvent,
                        new: EventUtils.new,
                    },
                    getColorOptions: EventUtils.getColorOptions,
                    getColorForGuestCalendar: EventUtils.getColorForGuestCalendar,
                    loadResources,
                    clearResources,
                    onGuestsChange,
                    guestCalendars,
                    getEventAccessLevel: EventUtils.getEventAccessLevel,
                    amIGuest: EventUtils.amIGuest,
                    toggleCalendarAsSelected,
                    getEventCalendarName: EventUtils.getEventCalendarName,
                    FiltersComponent: Filters,
                }}
            >
                {/* TODO: Move this clientId to an env var */}
                <GoogleOAuthProvider clientId="898813316937-kvsbqt0picob7t2ne4ha5s6n9nabtrjn.apps.googleusercontent.com">
                    <Switch>
                        <Route exact path="/master-calendar" component={GoogleCalendar} />
                        <Route exact path="/master-calendar/login" component={GoogleCalendarLogin} />
                    </Switch>
                </GoogleOAuthProvider>
            </CalendarContext.Provider>
        </Page>
    );
}
